<script setup>
import { computed, ref, watch } from 'vue';
import FilterButton from 'dashboard/components/ui/Dropdown/DropdownButton.vue';
import FilterListItemButton from 'dashboard/components/ui/Dropdown/DropdownListItemButton.vue';
import FilterDropdownEmptyState from 'dashboard/components/ui/Dropdown/DropdownEmptyState.vue';
import DropdownSearch from 'dashboard/components/ui/Dropdown/DropdownSearch.vue';

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  menuOption: {
    type: Array,
    default: () => [],
  },
  showMenu: {
    type: Boolean,
    default: false,
  },
  placeholderI18nKey: {
    type: String,
    default: '',
  },
  enableSearch: {
    type: Boolean,
    default: true,
  },
  emptyStateMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['toggleDropdown', 'addFilter', 'closeDropdown']);

const step = ref('root');
const selectedCategory = ref(null);
const searchTerm = ref('');

const resetNavigation = () => {
  step.value = 'root';
  selectedCategory.value = null;
  searchTerm.value = '';
};

watch(
  () => props.showMenu,
  visible => {
    if (!visible) resetNavigation();
  }
);

const toggleDropdown = () => emit('toggleDropdown');

const closeDropdown = () => {
  resetNavigation();
  emit('closeDropdown');
};

const openCategory = item => {
  selectedCategory.value = item;
  searchTerm.value = '';
  step.value = 'options';
};

const selectOption = option => {
  emit('addFilter', option);
  searchTerm.value = '';
};

const goBack = () => {
  resetNavigation();
};

const panelTitle = computed(() => selectedCategory.value?.name || '');

const activeCategory = computed(() => {
  if (!selectedCategory.value) return null;
  return (
    props.menuOption.find(item => item.id === selectedCategory.value.id) ||
    selectedCategory.value
  );
});

const searchPlaceholder = computed(() => {
  const type = activeCategory.value?.type;
  if (!type || !props.placeholderI18nKey) return '';
  return `${props.placeholderI18nKey}.${type.toUpperCase()}`;
});

const filteredOptions = computed(() => {
  const options = activeCategory.value?.options || [];
  const query = searchTerm.value.trim().toLowerCase();
  if (!query) return options;
  return options.filter(option =>
    String(option.name || '')
      .toLowerCase()
      .includes(query)
  );
});
</script>

<template>
  <FilterButton
    :button-text="name"
    icon="i-lucide-filter"
    @click="toggleDropdown"
  >
    <template v-if="showMenu" #dropdown>
      <div
        v-on-clickaway="closeDropdown"
        class="absolute z-20 left-0 md:right-0 top-10 w-[240px] max-h-[400px] flex flex-col overflow-hidden rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak shadow"
        @click.stop
      >
        <div
          v-if="step !== 'root'"
          class="flex items-center gap-2 px-2 h-10 border-b border-n-weak shrink-0"
        >
          <button
            type="button"
            class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
            @click="goBack"
          >
            <span class="i-lucide-arrow-left text-base" />
          </button>
          <span class="min-w-0 truncate text-sm font-medium text-n-slate-12">
            {{ panelTitle }}
          </span>
        </div>

        <DropdownSearch
          v-if="enableSearch && step === 'options'"
          v-model="searchTerm"
          :input-placeholder="$t(searchPlaceholder)"
        />

        <div class="overflow-y-auto">
          <template v-if="step === 'root'">
            <FilterDropdownEmptyState
              v-if="!menuOption.length"
              :message="emptyStateMessage"
            />
            <FilterListItemButton
              v-for="item in menuOption"
              :key="item.id"
              :button-text="item.name"
              @click.stop.prevent="openCategory(item)"
            />
          </template>

          <template v-else>
            <FilterDropdownEmptyState
              v-if="!filteredOptions.length"
              :message="
                emptyStateMessage || $t('REPORT.FILTER_ACTIONS.EMPTY_LIST')
              "
            />
            <FilterListItemButton
              v-for="option in filteredOptions"
              :key="option.id"
              :button-text="option.name"
              @click.stop.prevent="selectOption(option)"
            />
          </template>
        </div>
      </div>
    </template>
  </FilterButton>
</template>
